import { Injectable, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";

import { Observable, Subscription, map, distinctUntilChanged } from "rxjs";

import { ProductSizeVariant } from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import {
    AddToCartFacade,
    AddToCartStateBasket,
} from "@hermes/states/add-to-cart";
import { ProductPageFacade } from "@hermes/states/product-page";
import { FormService } from "@hermes/utils-generic/services/user-interface";

import type { BikiniSelectorComponent } from "../../bikini/components/bikini-selector/bikini-selector.component";
import type { LookSelectorComponent } from "../../look/components/look-selector/look-selector.component";

/**
 * This service is only used for BikiniSelectorComponent and lookSelectorComponent
 */
@Injectable()
export class ComplexProductSelectorService implements OnDestroy {
    public position: number;

    private component: BikiniSelectorComponent | LookSelectorComponent;

    /** Register subscription */
    private subscription: Subscription = new Subscription();

    #variantsForm = new FormGroup({
        sizeGroup: new FormControl(),
    });

    #sizeVariants: ProductSizeVariant[] | undefined = [];

    constructor(
        private formService: FormService,
        private context: Context,
        private addToCartFacade: AddToCartFacade,
        private productPageFacade: ProductPageFacade,
    ) {
        // We want reset variant previously selected variants.
        // If not, user can add product to cart without selected
        // variant if variant previously selected for the same product
        this.subscription.add(
            this.addToCartFacade.addToCartBasket$.subscribe(
                (addToCartStateBasket: AddToCartStateBasket) => {
                    if (
                        this.position !== undefined &&
                        addToCartStateBasket.notifDisplayed &&
                        addToCartStateBasket.totalItems > 0
                    ) {
                        this.addToCartFacade.changeButtonEnabledState(
                            this.position,
                            true,
                        );
                    }
                },
            ),
        );
    }

    public get variantsForm(): FormGroup {
        return this.#variantsForm;
    }

    public get sizeVariants(): ProductSizeVariant[] | undefined {
        return this.#sizeVariants;
    }

    public get sizeChanged$(): Observable<ProductSizeVariant | undefined> {
        return this.#variantsForm.valueChanges.pipe(
            map(() =>
                this.sizeVariants?.find(
                    (variant: ProductSizeVariant) =>
                        variant.size === this.#variantsForm.value.sizeGroup,
                ),
            ),
            distinctUntilChanged(),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.addToCartFacade.changeButtonEnabledState(this.position, true);

        if (this.component) {
            this.formService.unregisterLinkedControls(this.component);
        }
    }

    public selectedVariantSubscribtion(position: number): void {
        this.subscription.add(
            this.productPageFacade
                .selectedVariant(position)
                .subscribe((selectedVariant) => {
                    if (
                        !this.#variantsForm.get("sizeGroup").value &&
                        selectedVariant.size?.size
                    ) {
                        this.#variantsForm
                            .get("sizeGroup")
                            .setValue(selectedVariant.size.size);
                    }
                }),
        );
    }

    public setSizeVariants(
        sizeVariants: ProductSizeVariant[] | undefined,
        component?: BikiniSelectorComponent | LookSelectorComponent,
    ): void {
        this.#sizeVariants = sizeVariants;
        if (sizeVariants && sizeVariants.length > 0) {
            this.#variantsForm.setControl(
                "sizeGroup",
                new FormControl("", [Validators.required]),
                {
                    emitEvent: true,
                },
            );
        } else {
            this.#variantsForm.setControl("sizeGroup", new FormControl());
        }

        if (component && this.context.isInBrowserMode()) {
            this.component = component;
            this.formService.registerControl(
                this.component,
                this.#variantsForm,
            );
        }
    }
}
